import { useEffect, useRef } from 'react'
import { useGameContext } from '../../../screens/GameContainer/GameContext'
import { useGameQuery } from '../../../hooks/convex/useGameState'
import { toastNotificationStore } from '../../../components/ANewDesign/ToastNotification/ToastNotification'
import { translate } from '../../../i18n'

function getTeacherViewingCardsToastText(
  mode: string,
  userId: string,
  game: { cardVisibilityMode?: { mode: string; teacherUserId?: string; userIds?: string[] } } | null | undefined
): string | null {
  if (mode === 'allPublic') {
    return translate('gameScreen.teacherViewingCardsToast.allPublic')
  }
  if (mode === 'allPrivate' && game?.cardVisibilityMode?.mode === 'allPrivate') {
    const teacherUserId = game.cardVisibilityMode.teacherUserId ?? ''
    return userId === teacherUserId ? null : translate('gameScreen.teacherViewingCardsToast.allPrivate')
  }
  if (mode === 'byPlayer' && game?.cardVisibilityMode?.mode === 'byPlayer') {
    const userIds = game.cardVisibilityMode.userIds ?? []
    return userIds.includes(userId) ? translate('gameScreen.teacherViewingCardsToast.yourCards') : null
  }
  return null
}

/**
 * Listens to Convex game cardVisibilityMode and shows toasts when the teacher
 * is viewing cards. Use this in Convex-backed game screens.
 *
 * - allPrivate: "Teacher is viewing cards" (for non-teacher players)
 * - allPublic: "Teacher is viewing cards (public)"
 * - byPlayer: "Teacher is viewing your cards" when current user is in the viewed list
 *
 * ## Toast-store ownership
 *
 * The `toastNotificationStore` is shared across features (tournament moves,
 * teacher viewing, others). This hook is the ONLY owner of teacher-viewing
 * toasts; it must not clobber toasts set by other features.
 *
 * To stay scoped we track whether THIS hook posted the most recent teacher
 * toast (via `ownedTeacherToastRef`). We only call `hidden: true` when our
 * own toast is currently showing — never on first mount or when another
 * feature (e.g. `announceTableMoveComplete` post-table-move) has just set a
 * different message.
 */
const TOAST_DURATION_MS = 5000

export const useListenGameToastNotificationsConvex = () => {
  const { gameId, userId } = useGameContext()
  const gameState = useGameQuery(gameId, userId)
  const game = gameState?.game
  const mode = game?.cardVisibilityMode?.mode ?? 'default'
  // Tracks whether the most recent teacher toast we posted is still visible.
  // Toggles true when we set a teacher toast; toggled false when we clear it
  // ourselves or when another feature replaces it.
  const ownedTeacherToastRef = useRef(false)

  useEffect(() => {
    if (!gameId || !userId || mode === 'default') {
      // No teacher toast applicable. Only clear if we previously posted one;
      // otherwise leave the store alone so a table-move toast (or any other
      // cross-feature toast set during navigation) survives.
      if (ownedTeacherToastRef.current) {
        ownedTeacherToastRef.current = false
        toastNotificationStore.getState().dismiss()
      }
      return
    }
    const text = getTeacherViewingCardsToastText(mode, userId, game)
    if (text) {
      ownedTeacherToastRef.current = true
      // `show()` enqueues — if another feature's toast is currently visible,
      // ours waits its turn instead of clobbering. Use the explicit
      // duration so the teacher toast auto-hides like the others.
      toastNotificationStore.getState().show({ text, duration: TOAST_DURATION_MS })
    } else if (ownedTeacherToastRef.current) {
      ownedTeacherToastRef.current = false
      toastNotificationStore.getState().dismiss()
    }
  }, [gameId, mode])
}
